// CustomerDashboard.jsx - Mobile Optimized with PrimeReact UI - Enhanced Lucrative Design
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    Card, 
    Button, 
    DataTable, 
    Column, 
    Tag, 
    ProgressBar,
    Toast,
    Avatar,
    Dialog,
    Dropdown,
    Calendar,
    InputNumber,
    InputTextarea,
    Badge,
    Panel,
    Divider
} from 'primereact';

import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, isToday } from 'date-fns';
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";
import QuickBooking from './QuickBooking';

const API_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000/api';

const CustomerDashboard = () => {
    const auth = useAuth();
    const app = useApp();
    const api = useApi();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showQuickBookingDialog, setShowQuickBookingDialog] = useState(false);
    const [totalDue, setTotalDue] = useState(0);
    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadDashboardData();
        loadTotalDue();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}customer/dashboard`);
            
            if (response.data.status === 'success') {
                setDashboardData(response.data.data);
            } else {
                showError(response.data.message || 'Failed to load dashboard');
            }
        } catch (error) {
            console.error('Error loading dashboard:', error);
            showError('Failed to load dashboard data. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const loadTotalDue = async () => {
        try {
            const response = await axios.get(`${API_URL}/customer/payments/total-due`);
            if (response.data.status === 'success') {
                setTotalDue(response.data.totalDue || 0);
            }
        } catch (error) {
            console.error('Error loading total due:', error);
            setTotalDue(0);
        }
    };

    const showError = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 3000
            });
        }
    };

    const showSuccess = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: message,
                life: 3000
            });
        }
    };

    const getStatusSeverity = (status) => {
        if (!status) return null;
        const statusLower = status.toLowerCase();
        if (statusLower.includes('complete')) return 'success';
        if (statusLower.includes('process') || statusLower.includes('progress')) return 'info';
        if (statusLower.includes('pending')) return 'warning';
        if (statusLower.includes('cancel')) return 'danger';
        return 'info';
    };

    const handleQuickBookingSuccess = () => {
        setShowQuickBookingDialog(false);
        showSuccess('Booking created successfully!');
        loadDashboardData();
        loadTotalDue();
    };

    const handleQuickBookingCancel = () => {
        setShowQuickBookingDialog(false);
    };

    const handlePayNow = () => {
        navigate('/payments/paymentcustomer');
    };

    // Function to open Quick Booking
    const openQuickBooking = (e) => {
        e.stopPropagation();
        console.log('Opening Quick Booking');
        setShowQuickBookingDialog(true);
    };

    if (loading) {
        return (
            <div className="grid p-fluid">
                <div className="col-12">
                    <Card className="border-round shadow-2">
                        <div className="flex flex-column align-items-center justify-content-center py-8">
                            <ProgressBar mode="indeterminate" style={{ height: '6px', width: '200px' }} className="mb-3"></ProgressBar>
                            <span className="text-primary font-medium">Loading dashboard...</span>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div className="flex flex-column align-items-center justify-content-center min-h-screen p-4">
                <Card className="shadow-3 text-center p-4">
                    <i className="pi pi-exclamation-triangle text-6xl text-yellow-500 mb-4"></i>
                    <h3 className="text-2xl font-medium mb-3">No Dashboard Data</h3>
                    <p className="text-gray-600 mb-4">Unable to load dashboard data. Please try again.</p>
                    <Button 
                        label="Retry" 
                        icon="pi pi-refresh" 
                        onClick={loadDashboardData}
                        className="p-button-lg"
                    />
                </Card>
            </div>
        );
    }

    const { user, stats, activebookings = [], recentbookings = [], upcomingpickups = [] } = dashboardData;

    // Colorful gradient configurations
    const gradientColors = {
        primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        warning: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        info: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        purple: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
        orange: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
        teal: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        pink: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    };

    // Mobile responsive render functions
    const renderMobileStats = () => (
        <div className="grid">
            {[
                { 
                    label: 'Total Bookings', 
                    value: stats?.totalbookings || 0, 
                    icon: 'pi-shopping-bag', 
                    gradient: gradientColors.primary,
                    textColor: '#667eea'
                },
                { 
                    label: 'Completed', 
                    value: stats?.completedbookings || 0, 
                    icon: 'pi-check-circle', 
                    gradient: gradientColors.success,
                    textColor: '#11998e'
                },
                { 
                    label: 'Active', 
                    value: stats?.activebookings || 0, 
                    icon: 'pi-spinner', 
                    gradient: gradientColors.warning,
                    textColor: '#f5576c'
                },
                { 
                    label: 'Total Spent', 
                    value: `$${(stats?.totalspent || 0).toFixed(2)}`, 
                    icon: 'pi-dollar', 
                    gradient: gradientColors.purple,
                    textColor: '#a18cd1'
                }
            ].map((stat, index) => (
                <div key={index} className="col-6 mb-3">
                    <Card 
                        className="shadow-2 border-round-xl stat-card" 
                        style={{ 
                            background: stat.gradient,
                            border: 'none',
                            overflow: 'hidden'
                        }}
                    >
                        <div className="flex align-items-center p-2">
                            <div className="flex align-items-center justify-content-center border-circle mr-3" 
                                style={{ 
                                    backgroundColor: 'rgba(255,255,255,0.25)', 
                                    width: '56px', 
                                    height: '56px',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)'
                                }}>
                                <i className={`pi ${stat.icon} text-white`} style={{ fontSize: '1.8rem' }}></i>
                            </div>
                            <div className="flex flex-column text-white">
                                <span className="text-3xl font-bold">{stat.value}</span>
                                <span className="text-sm opacity-90 font-medium">{stat.label}</span>
                            </div>
                        </div>
                    </Card>
                </div>
            ))}
        </div>
    );

    const renderMobileBookingItem = (booking, isActive = true) => (
        <div key={booking.id} className="surface-card border-round-xl p-3 mb-3 shadow-2 booking-item" 
            style={{ borderLeft: `4px solid ${isActive ? '#667eea' : '#11998e'}` }}>
            <div className="flex justify-content-between align-items-start mb-2">
                <div className="flex-grow-1">
                    <div className="flex align-items-center gap-2 mb-2">
                        <span className="font-semibold text-lg">{booking.service?.name || 'Service'}</span>
                        <Tag 
                            value={booking.status} 
                            severity={getStatusSeverity(booking.status)}
                            className="text-xs px-2 py-1"
                            rounded
                        />
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600 mb-1">
                        <i className="pi pi-hashtag text-primary"></i>
                        <span>{booking.bookingreference}</span>
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600">
                        <i className="pi pi-calendar text-primary"></i>
                        <span>
                            Pickup: {booking.pickupdate ? format(new Date(booking.pickupdate), 'MMM dd, yyyy') : 'N/A'}
                            {booking.pickupdate && isToday(new Date(booking.pickupdate)) && 
                                <Badge value="Today" severity="success" className="ml-2"></Badge>}
                        </span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-bold" style={{ color: '#667eea' }}>₹{(booking.totalamount || 0).toFixed(2)}</div>
                    <Button 
                        icon="pi pi-eye" 
                        className="p-button-rounded p-button-outlined p-button-sm"
                        style={{ borderColor: '#667eea', color: '#667eea' }}
                        onClick={() => navigate(`/bookings/view/${booking.id}`)}
                        tooltip="View Details"
                        tooltipOptions={{ position: 'left' }}
                    />
                </div>
            </div>
        </div>
    );

    const renderMobileUpcomingPickup = (booking, index) => (
        <div key={booking.id || index} className="surface-card border-round-xl p-3 mb-3 shadow-2 pickup-item"
            style={{ 
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.1)'
            }}>
            <div className="flex align-items-start">
                <div className="flex align-items-center justify-content-center border-circle mr-3" 
                    style={{ 
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        width: '48px', 
                        height: '48px',
                        boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)'
                    }}>
                    <i className="pi pi-calendar text-white"></i>
                </div>
                <div className="flex-grow-1">
                    <div className="flex align-items-center justify-content-between mb-2">
                        <span className="font-semibold text-lg">{booking.service?.name || 'Service'}</span>
                        <Tag 
                            value={booking.status} 
                            severity={getStatusSeverity(booking.status)}
                            className="text-xs"
                            rounded
                        />
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600 mb-1">
                        <i className="pi pi-clock" style={{ color: '#f5576c' }}></i>
                        <span>
                            {booking.pickupdate ? 
                                format(new Date(booking.pickupdate), 'EEE, MMM dd • hh:mm a') : 'Date not set'}
                        </span>
                    </div>
                    <div className="flex align-items-center gap-2 text-sm text-600">
                        <i className="pi pi-map-marker" style={{ color: '#f5576c' }}></i>
                        <span className="text-600">{booking.pickupaddress || 'Address not specified'}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="customer-dashboard">
            <Toast ref={toast} position="top-center" />
            
            {/* Mobile Header with Animated Gradient */}
            <div className="header-container mb-4">
                <div className="header-content p-4">
                    <div className="flex align-items-center justify-content-between mb-3">
                        <div className="header-text">
                            <span className="text-white text-sm font-medium opacity-90">Welcome back,</span>
                            <h1 className="text-3xl font-bold text-white mt-1">{user?.fullname?.split(' ')[0] || 'Customer'}! 👋</h1>
                            <p className="text-white opacity-80 mt-2">Your premium laundry experience</p>
                        </div>
                        <div className="avatar-container relative">
                            <Avatar 
                                icon="pi pi-user" 
                                size="xlarge"
                                className="avatar-gradient shadow-3"
                                style={{ 
                                    width: '68px', 
                                    height: '68px', 
                                    fontSize: '2.2rem',
                                    background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
                                    color: '#667eea',
                                    border: '3px solid rgba(255,255,255,0.3)'
                                }}
                            />
                            {stats?.activebookings > 0 && (
                                <Badge 
                                    value={stats.activebookings} 
                                    severity="danger" 
                                    className="absolute pulse-badge"
                                    style={{ 
                                        top: '-4px', 
                                        right: '-4px',
                                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                        fontSize: '0.8rem',
                                        minWidth: '24px',
                                        height: '24px',
                                        padding: '0 8px'
                                    }}
                                ></Badge>
                            )}
                        </div>
                    </div>
                    
                    {/* Attractive Action Buttons */}
                    <div className="flex gap-3 mt-4">
                        <Button 
                            label="✨ Book Now" 
                            icon="pi pi-plus-circle" 
                            className="p-button-lg flex-1 action-button-primary"
                            style={{ 
                                borderRadius: '50px',
                                background: 'linear-gradient(135deg, #fff 0%, #f0f0f0 100%)',
                                color: '#667eea',
                                border: 'none',
                                fontWeight: 'bold',
                                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                                padding: '0.8rem 1.5rem'
                            }}
                            onClick={() => navigate('/bookingquick')}
                            tooltip='Full Booking with Items'
                            tooltipOptions={{ position: 'bottom' }}
                        />
                        
                        {/* Quick Booking Round Button with Icon */}
                        <div className="quick-book-wrapper">
                            <Button 
                                icon="pi pi-bolt" 
                                className="p-button-rounded p-button-lg quick-book-btn"
                                style={{ 
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                                    color: 'white',
                                    border: 'none',
                                    boxShadow: '0 8px 25px rgba(253, 160, 133, 0.4)',
                                    fontSize: '1.8rem',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={openQuickBooking}
                                tooltip="⚡ Quick Book"
                                tooltipOptions={{ position: 'bottom' }}
                            />
                            <span className="quick-book-label">Quick</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-3">
                {/* Mobile Stats - Colorful Cards */}
                {renderMobileStats()}

                {/* Total Due Card - Enhanced */}
                {totalDue > 0 && (
                    <Card className="mb-4 shadow-3 border-round-xl total-due-card" 
                        style={{ 
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            cursor: 'pointer',
                            border: 'none',
                            overflow: 'hidden'
                        }} 
                        onClick={handlePayNow}>
                        <div className="flex align-items-center justify-content-between p-2">
                            <div className="flex align-items-center">
                                <div className="flex align-items-center justify-content-center border-circle mr-3" 
                                    style={{ 
                                        backgroundColor: 'rgba(255,255,255,0.25)', 
                                        width: '56px', 
                                        height: '56px',
                                        backdropFilter: 'blur(10px)'
                                    }}>
                                    <i className="pi pi-credit-card text-white" style={{ fontSize: '1.8rem' }}></i>
                                </div>
                                <div>
                                    <div className="text-white opacity-90 text-sm font-medium mb-1">💳 Total Due</div>
                                    <div className="text-white text-3xl font-bold">₹{totalDue.toFixed(2)}</div>
                                </div>
                            </div>
                            <Button 
                                label="Pay Now →" 
                                icon="pi pi-arrow-right" 
                                className="p-button-lg"
                                style={{ 
                                    background: 'rgba(255,255,255,0.2)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    color: 'white',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '50px',
                                    fontWeight: 'bold'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePayNow();
                                }}
                            />
                        </div>
                    </Card>
                )}

                {/* Mobile Active Bookings */}
                <Panel header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-briefcase" style={{ color: '#667eea' }}></i>
                        <span className="font-bold">📋 Active Bookings</span>
                        {activebookings.length > 0 && (
                            <Badge value={activebookings.length} severity="info" className="ml-2"></Badge>
                        )}
                    </div>
                } className="mb-4 shadow-2 panel-custom" toggleable>
                    {activebookings.length === 0 ? (
                        <div className="text-center py-6">
                            <i className="pi pi-inbox text-5xl" style={{ color: '#d1d5db' }}></i>
                            <p className="text-600 text-lg mb-4">No active bookings</p>
                            <Button 
                                label="📦 Book Now" 
                                icon="pi pi-plus"
                                onClick={() => navigate('/bookingquick')}
                                className="p-button-lg"
                                style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    borderRadius: '50px'
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            <div className="max-h-25rem overflow-auto">
                                {activebookings.map(booking => renderMobileBookingItem(booking, true))}
                            </div>
                            <Divider />
                            <Button 
                                label="📊 View All Bookings" 
                                icon="pi pi-arrow-right"
                                className="p-button-text w-full p-button-lg"
                                style={{ color: '#667eea' }}
                                onClick={() => navigate('/bookings/listcustomer')}
                            />
                        </div>
                    )}
                </Panel>

                {/* Mobile Upcoming Pickups */}
                <Panel header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-calendar" style={{ color: '#f5576c' }}></i>
                        <span className="font-bold">🚀 Upcoming Pickups</span>
                    </div>
                } className="mb-4 shadow-2 panel-custom" toggleable>
                    {upcomingpickups.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="pi pi-calendar-times text-5xl" style={{ color: '#d1d5db' }}></i>
                            <p className="text-600">No upcoming pickups</p>
                        </div>
                    ) : (
                        <div>
                            {upcomingpickups.map((booking, index) => renderMobileUpcomingPickup(booking, index))}
                        </div>
                    )}
                </Panel>

                {/* Mobile Recent Bookings */}
                <Panel header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-history" style={{ color: '#11998e' }}></i>
                        <span className="font-bold">⏳ Recent Activity</span>
                    </div>
                } className="mb-4 shadow-2 panel-custom" toggleable>
                    {recentbookings.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="pi pi-history text-5xl" style={{ color: '#d1d5db' }}></i>
                            <p className="text-600">No recent activity</p>
                        </div>
                    ) : (
                        <div>
                            {recentbookings.slice(0, 3).map(booking => (
                                <div key={booking.id} className="flex justify-content-between align-items-center p-3 surface-card border-round-xl mb-2 shadow-1 recent-item"
                                    style={{ 
                                        background: 'linear-gradient(135deg, rgba(17, 153, 142, 0.05) 0%, rgba(56, 239, 125, 0.05) 100%)',
                                        borderLeft: '3px solid #11998e'
                                    }}>
                                    <div>
                                        <div className="font-semibold text-lg mb-1">{booking.service?.name || 'Service'}</div>
                                        <div className="flex align-items-center gap-2 text-sm text-600">
                                            <i className="pi pi-clock" style={{ color: '#11998e' }}></i>
                                            <span>
                                                {booking.createdat ? 
                                                    formatDistanceToNow(new Date(booking.createdat), { addSuffix: true }) : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xl font-bold" style={{ color: '#11998e' }}>₹{(booking.totalamount || 0).toFixed(2)}</div>
                                        <Tag 
                                            value={booking.status} 
                                            severity={getStatusSeverity(booking.status)}
                                            rounded
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                {/* Mobile Quick Actions - Enhanced */}
                <Panel header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-th-large" style={{ color: '#764ba2' }}></i>
                        <span className="font-bold">⚡ Quick Actions</span>
                    </div>
                } className="mb-4 shadow-2 panel-custom">
                    <div className="grid quick-actions">
                        <div className="col-6 mb-3">
                            <Button 
                                label="🚚 Schedule" 
                                icon="pi pi-truck" 
                                className="p-button-lg w-full action-btn"
                                style={{
                                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                    border: 'none',
                                    borderRadius: '50px',
                                    color: 'white',
                                    boxShadow: '0 4px 15px rgba(79, 172, 254, 0.3)'
                                }}
                                onClick={() => navigate('/bookingquick')}
                            />
                        </div>
                        <div className="col-6 mb-3">
                            <Button 
                                label="📋 Bookings" 
                                icon="pi pi-list" 
                                className="p-button-lg w-full action-btn"
                                style={{
                                    background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
                                    border: 'none',
                                    borderRadius: '50px',
                                    color: 'white',
                                    boxShadow: '0 4px 15px rgba(161, 140, 209, 0.3)'
                                }}
                                onClick={() => navigate('/bookings/listcustomer')}
                            />
                        </div>
                        <div className="col-6">
                            <Button 
                                label="💳 Payments" 
                                icon="pi pi-wallet" 
                                className="p-button-lg w-full action-btn"
                                style={{
                                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                    border: 'none',
                                    borderRadius: '50px',
                                    color: 'white',
                                    boxShadow: '0 4px 15px rgba(67, 233, 123, 0.3)'
                                }}
                                onClick={() => navigate('/payments/paymentcustomer')}
                            />
                        </div>
                        <div className="col-6">
                            <Button 
                                label="👤 Profile" 
                                icon="pi pi-user-edit" 
                                className="p-button-lg w-full action-btn"
                                style={{
                                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                    border: 'none',
                                    borderRadius: '50px',
                                    color: 'white',
                                    boxShadow: '0 4px 15px rgba(250, 112, 154, 0.3)'
                                }}
                                onClick={() => navigate('/account')}
                            />
                        </div>
                    </div>
                </Panel>

                {/* Mobile Loyalty Points - Enhanced */}
                <Card className="mb-5 shadow-3 loyalty-card" 
                    style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        overflow: 'hidden'
                    }}>
                    <div className="text-center py-4">
                        <div className="flex align-items-center justify-content-center gap-2 mb-3">
                            <i className="pi pi-star text-5xl text-yellow-300 animate-pulse"></i>
                            <h3 className="text-white text-2xl font-bold mb-0">🌟 Loyalty Rewards</h3>
                        </div>
                        <div className="text-white text-6xl font-bold mb-2">{user?.loyaltypoints || 0}</div>
                        <div className="text-white text-sm opacity-90 mb-4">✨ Points Available</div>
                        
                        <div className="mb-4 px-4">
                            <div className="relative">
                                <ProgressBar 
                                    value={(user?.loyaltypoints || 0) % 100} 
                                    showValue={false}
                                    style={{ 
                                        height: '12px', 
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        borderRadius: '50px'
                                    }}
                                    className="mb-2"
                                    color="var(--yellow-300)"
                                />
                                <div className="text-white text-sm font-medium">
                                    🎯 {Math.min((user?.loyaltypoints || 0) % 100, 100)}/100 to next reward
                                </div>
                            </div>
                        </div>
                        
                        <Button 
                            label="🎁 Redeem Points" 
                            icon="pi pi-gift" 
                            className="p-button-lg text-white"
                            disabled={(user?.loyaltypoints || 0) < 100}
                            style={{ 
                                background: 'rgba(255,255,255,0.2)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: '50px',
                                backdropFilter: 'blur(10px)',
                                padding: '0.8rem 2rem',
                                fontWeight: 'bold'
                            }}
                        />
                    </div>
                </Card>
            </div>

            {/* Mobile Bottom Navigation - Colorful */}
            <div className="bottom-nav">
                <div className="flex justify-content-around p-2">
                    {[
                        { icon: '🏠', label: 'Home', path: '/', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
                        { icon: '🧺', label: 'Book', path: '/bookingquick', gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' },
                        { icon: '🔔', label: 'Alerts', path: '/customernotifications/notify', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
                        { icon: '🤼‍♀️', label: 'Price Compare', path: '/pricecompare', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }
                    ].map((item, index) => (
                        <div key={index} className="text-center nav-item">
                            <div 
                                className="nav-icon-container"
                                onClick={() => navigate(item.path)}
                                style={{ 
                                    background: item.gradient,
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.1)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                                }}
                            >
                                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>{item.icon}</span>
                            </div>
                            <div className="text-xs mt-2 text-600 font-medium">{item.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quick Booking Dialog */}
      <Dialog
    header={
        <div className="flex align-items-center gap-2" >
            <i className="pi pi-bolt" style={{ fontSize: '1.5rem', color: '#dec6bf' }}></i>
            <span className="text-xl font-bold" style={{ color: '#667eea' }}>Quick Booking</span>
        </div>
    }
    
    visible={showQuickBookingDialog}
    onHide={handleQuickBookingCancel}
    className="w-11 quick-booking-dialog"
    breakpoints={{ '960px': '75vw', '640px': '95vw' }}
    style={{ borderRadius: '2rem' }}
    modal
    dismissableMask
    closable
    showHeader={true}
    footer={null}
    // Add custom close icon
    closeIcon={<i className="pi pi-times" style={{ color: '#ff0000' }} />}
>
    <div className="quick-booking-container">
        <QuickBooking 
            onSuccess={handleQuickBookingSuccess}
            onCancel={handleQuickBookingCancel}
        />
    </div>
</Dialog>


            <style>{`
                .customer-dashboard {
                    padding-bottom: 100px;
                    background: var(--surface-ground);
                    min-height: 100vh;
                }
                
                /* Header with animated gradient */
                .header-container {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 0 0 2rem 2rem;
                    position: relative;
                    overflow: hidden;
                }
                
                .header-container::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
                    animation: shimmer 10s infinite linear;
                }
                
                @keyframes shimmer {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .header-content {
                    position: relative;
                    z-index: 1;
                }
                
                .avatar-container .p-avatar {
                    border: 3px solid rgba(255,255,255,0.3);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                }
                
                .pulse-badge {
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.1); }
                    100% { transform: scale(1); }
                }
                
                .action-button-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4) !important;
                }
                
                /* Quick Book Button Styles */
                .quick-book-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                }
                
                .quick-book-btn {
                    transition: all 0.3s ease !important;
                    animation: pulse-glow 2s infinite;
                }
                
                .quick-book-btn:hover {
                    transform: scale(1.1) !important;
                    box-shadow: 0 12px 35px rgba(253, 160, 133, 0.6) !important;
                }
                
                .quick-book-btn:active {
                    transform: scale(0.95) !important;
                }
                
                @keyframes pulse-glow {
                    0% {
                        box-shadow: 0 8px 25px rgba(253, 160, 133, 0.4);
                    }
                    50% {
                        box-shadow: 0 8px 35px rgba(253, 160, 133, 0.7), 0 0 60px rgba(253, 160, 133, 0.2);
                    }
                    100% {
                        box-shadow: 0 8px 25px rgba(253, 160, 133, 0.4);
                    }
                }
                
                .quick-book-label {
                    font-size: 0.65rem;
                    font-weight: 600;
                    color: #fda085;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-top: 2px;
                }
                
                /* Stat cards */
                .stat-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 30px rgba(0,0,0,0.15) !important;
                }
                
                /* Total due card */
                .total-due-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .total-due-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 35px rgba(250, 112, 154, 0.3) !important;
                }
                
                /* Panel custom */
                .panel-custom .p-panel-header {
                    background: white;
                    border-radius: 1rem 1rem 0 0;
                    padding: 1rem 1.25rem;
                    border-bottom: 2px solid #f3f4f6;
                }
                
                .panel-custom .p-panel-content {
                    border-radius: 0 0 1rem 1rem;
                    padding: 1rem;
                }
                
                /* Booking items */
                .booking-item {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .booking-item:hover {
                    transform: translateX(4px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
                }
                
                .pickup-item {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .pickup-item:hover {
                    transform: translateX(4px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
                }
                
                .recent-item {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .recent-item:hover {
                    transform: translateX(4px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
                }
                
                /* Quick actions */
                .action-btn {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .action-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.2) !important;
                }
                
                /* Loyalty card */
                .loyalty-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .loyalty-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.3) !important;
                }
                
                .animate-pulse {
                    animation: pulse 2s infinite;
                }
                
                /* Bottom navigation */
                .bottom-nav {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    background: rgba(255,255,255,0.95);
                    backdrop-filter: blur(10px);
                    box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
                    padding: 0.5rem 0 0.5rem 0;
                }
                
                .nav-icon-container {
                    transition: all 0.3s ease;
                }
                
                .nav-icon-container:active {
                    transform: scale(0.9) !important;
                }
                
                /* Quick Booking Dialog */
                .quick-booking-dialog .p-dialog-header {
                    border-radius: 2rem 2rem 0 0;
                    padding: 1.5rem;
                    background: white;
                    border-bottom: 2px solid #f3f4f6;
                }
                
                .quick-booking-dialog .p-dialog-content {
                    border-radius: 0 0 2rem 2rem;
                    padding: 1.5rem;
                    max-height: 80vh;
                    overflow-y: auto;
                }
                
                .quick-booking-container {
                    width: 100%;
                }
                
                /* Touch-friendly tap targets */
                @media (max-width: 768px) {
                    .p-button {
                        min-height: 48px;
                    }
                    
                    .p-button.p-button-lg {
                        min-height: 56px;
                    }
                    
                    .p-inputtext {
                        min-height: 48px;
                    }
                    
                    .p-inputtext-lg {
                        min-height: 56px;
                        font-size: 1rem;
                    }
                    
                    .text-2xl {
                        font-size: 1.75rem;
                    }
                    
                    .text-3xl {
                        font-size: 2rem;
                    }
                    
                    .text-4xl {
                        font-size: 2.25rem;
                    }
                    
                    .text-5xl {
                        font-size: 2.5rem;
                    }
                    
                    .text-6xl {
                        font-size: 3rem;
                    }
                    
                    .quick-booking-dialog .p-dialog-content {
                        padding: 1rem;
                    }
                    
                    .quick-book-btn {
                        width: 56px !important;
                        height: 56px !important;
                        font-size: 1.5rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default CustomerDashboard;